'use client';

import React, {
  forwardRef,
  JSX,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { getCookie } from 'cookies-next';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';

import orNavBarVariants from './or-navbar.variants';
import SkipToMainButton from './skip-to-main';
import { openChat } from '@/components/integrations/GenesysLoader';

// components
import { AtNavLink } from '@/components/atoms/at-nav-link/at-nav-link';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtSetLocationAction } from '@/components/atoms/at-set-location-action/at-set-location-action';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtLink } from '@/components/atoms/at-link/at-link';
import { AtLogo } from '@/components/atoms/at-logo/at-logo';
import {
  MlAccordion,
  MlAccordionItem,
} from '@/components/molecules/ml-accordion/ml-accordion';
import { MlCtaCard } from '@/components/molecules/ml-cta-card/ml-cta-card';

// types
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import {
  EThemes,
  IAtLinkGlobalFieldProps,
  IMlCtaCardBlockCS,
} from '@/lib/contentstack-sdk/types';
import { EAccordionItemIntents } from '@/components/molecules/ml-accordion/ml-accordion.types';
import {
  IOrNavbarProps,
  ILinkList,
  ILinkContent,
  IMainCTAsProps,
  IFooterProps,
  IOrNavbarLinksProps,
  ITopNavProps,
} from './or-navbar.types';
import { ENavLinkVariants } from '@/components/atoms/at-nav-link/at-nav-link.types';
import {
  EDividerDirectionalVariants,
  EDividerVariants,
  EDividerColors,
} from '@/components/atoms/at-divider/at-divider.types';
import { ESetLocationActionVariants } from '@/components/atoms/at-set-location-action/at-set-location-action.types';

// hooks
import { useGlobalBanner } from '@/context/globalBannerContext';
import { useLocation } from '@/context/locationContext';
import {
  ESetLocationType,
  useSetLocationModal,
} from '@/context/setLocationContext';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useLockBodyScroll } from '@uidotdev/usehooks';
import { LoginModalContext } from '@/context/loginModalContext';
import { useAuth } from '@/context/authContext';
import { useRouter } from 'next/navigation';

// utils
import { groupLinksByTitle, IGroupedLinkContent, IGroupedLinks } from './utils';
import { parseIconFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';
import { accountUrl } from '@/constants';

const navContainerAnimation = {
  visible: {
    opacity: 1,
    transition: {
      x: { velocity: 100 },
      duration: 0.1,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      x: { velocity: 100 },
      duration: 0.1,
    },
  },
};

export const OrNavbar = ({
  links,
  topNavActions,
  chat,
  phoneNumber,
}: IOrNavbarProps): JSX.Element => {
  const [isToggled, setToggle] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(0);
  const [currentSublinks, setCurrentSublinks] = useState<any>({});
  const closeRef = useRef<HTMLButtonElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { isBannerVisible } = useGlobalBanner();
  const theme = EThemes.LIGHT;
  const bp = useBreakpoint();

  useEffect(() => {
    const grouped = groupLinksByTitle(
      links?.[selected]?.nav_item.sub_links as IGroupedLinkContent[],
    );
    const groupedWithTitle = grouped?.withTitle?.reduce<{
      [key: string]: IGroupedLinkContent[];
    }>((acc, item) => {
      // Group items by their title
      (acc[item.links.title] = acc[item.links.title] || []).push(item);

      return acc;
    }, {});

    setCurrentSublinks({
      withoutTitle: grouped.withoutTitle,
      withTitle: groupedWithTitle,
    });
  }, [selected, links]);

  const { base } = orNavBarVariants({ isToggled });

  useEffect(() => {
    // do not trigger if in testing env (jest)
    if (process.env.NODE_ENV !== 'test' && isToggled) {
      setToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // navbar slide-to-top effect
  useEffect(() => {
    const handleScroll = () => {
      if (bannerRef.current) {
        const banner = document.getElementById('orGlobalBanner');
        const bannerHeight = banner ? banner.offsetHeight : 0;
        const newTop = Math.max(0, bannerHeight - window.scrollY);
        bannerRef.current.style.top = `${newTop}px`;
      }
    };

    // Add scroll event & initialize on mount
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [bp, isBannerVisible]);

  return (
    <>
      <SkipToMainButton />
      <header className="w-full">
        <nav
          ref={bannerRef}
          data-theme={theme}
          className={classNames([base(), 'bg-surface-primary'], {
            '!top-0 md:h-[calc(100dvh)]': isToggled,
          })}
        >
          <div
            className={`md:container-none relative grid min-h-16 transition-none md:grid-cols-4`}
          >
            <TopBarNavigation
              setToggle={setToggle}
              isToggled={isToggled}
              topNavActions={topNavActions}
              closeRef={closeRef}
            />
            {isToggled && (
              <AtDivider
                customClasses="col-span-4 hidden md:block md:ml-8 md:!w-[calc(100vw_-_78px)] lg:!w-full lg:ml-0 mt-4"
                direction={EDividerDirectionalVariants.HORIZONTAL}
                decorative
                intent={EDividerVariants.THIN}
                color={EDividerColors.GREY}
              />
            )}

            <AnimatePresence>
              {isToggled && (
                <motion.div
                  className="z-10 col-span-4 rounded-lg text-black lg:col-span-4"
                  initial="hidden"
                  animate={isToggled ? 'visible' : 'hidden'}
                  exit="hidden"
                  id="main-menu"
                  variants={navContainerAnimation}
                >
                  {/* DESKTOP */}
                  <DesktopNavBody
                    isActive={isToggled}
                    selected={selected}
                    setSelected={setSelected}
                    links={links}
                    currentSublinks={currentSublinks}
                    setToggle={setToggle}
                    closeRef={closeRef}
                    chatProps={chat}
                    phoneNumber={phoneNumber}
                  />

                  {/* MOBILE */}
                  <MobileNavBody
                    topNavActions={topNavActions}
                    phoneNumber={phoneNumber}
                    links={links}
                    isMenuOpen={isToggled}
                    closeRef={closeRef}
                    chatProps={chat}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>
      </header>
    </>
  );
};

interface DesktopNavBodyProps {
  links: ILinkList[];
  selected: number;
  setSelected: React.Dispatch<SetStateAction<number>>;
  currentSublinks: IGroupedLinks;
  setToggle: React.Dispatch<SetStateAction<boolean>>;
  closeRef?: React.RefObject<HTMLButtonElement | null>;
  isActive: boolean;
  chatProps: {
    content: string;
  };
  phoneNumber: string;
}

const DesktopNavBody = ({
  links,
  selected,
  setSelected,
  currentSublinks,
  setToggle,
  closeRef,
  isActive,
  chatProps,
  phoneNumber,
}: DesktopNavBodyProps) => {
  const mainLinksRef = useRef<HTMLDivElement>(null);
  const imageCardRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const subLinkMenuRef = useRef<HTMLDivElement>(null);

  const bp = useBreakpoint();

  // Manages focus state for all of the mega menu based on requirements from QA
  useEffect(() => {
    const subLinks: HTMLAnchorElement[] = Array.from(
      (subLinkMenuRef?.current as HTMLDivElement)?.querySelectorAll(`a`),
    );

    const mainLinks: HTMLButtonElement[] = Array.from(
      (mainLinksRef.current as HTMLDivElement)?.querySelectorAll('button'),
    ) as HTMLButtonElement[];

    const imageCardFocusable: HTMLAnchorElement | undefined | null =
      imageCardRef?.current?.querySelector('a');

    const footerFocusable: HTMLButtonElement | undefined | null =
      footerRef?.current?.querySelector('button');

    // Define the logic to handle Tab press on the last sublink
    const handleTabOnLastSubLink = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        // Check if Tab was pressed without Shift
        // Focus the main links container or the first link in the main links
        event.preventDefault();

        if (bp === 'md' || bp === 'lg') {
          if (
            subLinks &&
            subLinks.length > 0 &&
            document.activeElement === subLinks[0]
          ) {
            return subLinks[0].focus();
          }

          if (document.activeElement === subLinks[subLinks.length - 1]) {
            return subLinks[0].focus();
          }
        }
      }
    };

    const handleBackTabOnFirstSublink = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        if (
          subLinks &&
          subLinks.length > 0 &&
          document.activeElement === subLinks[0]
        ) {
          return imageCardFocusable?.focus();
        }
      }
    };

    const handleImageCardFocus = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        if (
          subLinks &&
          subLinks.length > 0 &&
          document.activeElement === imageCardFocusable
        ) {
          return subLinks[0].focus();
        }
      }

      if (event.key === 'ArrowLeft' || event.key === 'Escape') {
        event.preventDefault();
        const currentlySelectedLinkIndex = mainLinks?.findIndex(
          (link) => link.getAttribute('data-selected') == 'true',
        );

        return mainLinks[currentlySelectedLinkIndex]?.focus();
      }
    };

    const handleExitMain = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setToggle(false);
        closeRef?.current?.focus();
      }
    };

    const handleExitSub = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const currentlySelectedLinkIndex = mainLinks?.findIndex(
          (link) => link.getAttribute('data-selected') == 'true',
        );

        return mainLinks[currentlySelectedLinkIndex]?.focus();
      }
    };

    const handleArrowPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        subLinks[0].focus();
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const currentlySelectedLinkIndex = mainLinks?.findIndex(
          (link) => link.getAttribute('data-selected') == 'true',
        );

        return mainLinks[currentlySelectedLinkIndex]?.focus();
      }
    };

    const handleFooterFocus = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();

        if (mainLinks[mainLinks.length - 1] === document.activeElement) {
          closeRef?.current?.focus();
        }
      }
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();

        if (mainLinks[mainLinks.length - 1] === document.activeElement) {
          mainLinks[mainLinks.length - 2].focus();
        }
      }
    };

    const handleCloseFocus = (event: KeyboardEvent) => {
      // Work around for isActive, since the state doesn't get passed down then this component isn't rendered.
      // Evaluate if the menuRef is available, and if not, use standard tab behaviour
      if (mainLinksRef?.current) {
        if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();
          footerFocusable?.focus();
        }

        if (event.key === 'Tab' && !event.shiftKey) {
          event.preventDefault();
          mainLinks[0]?.focus();
        }
      }
    };

    if (subLinks.length > 0) {
      // Handle Tab on the last sublink to cycle back to main links
      const lastSubLink = subLinks[subLinks.length - 1];

      if (bp === 'md' || bp === 'lg') {
        lastSubLink.addEventListener('keydown', handleTabOnLastSubLink);
      }

      if (bp === 'xl' || bp === '2xl' || bp === '3xl') {
        imageCardFocusable?.addEventListener('keydown', handleImageCardFocus);
      }
      subLinks[0].addEventListener('keydown', handleBackTabOnFirstSublink);
    }

    const handleMainTabFocus = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        closeRef?.current?.focus();
      }
    };

    const handleMainClick = (event: MouseEvent) => {
      event.preventDefault();
      subLinks[0]?.focus();
    };

    subLinks.forEach((link) => {
      link.addEventListener('keydown', handleArrowPress);
      link.addEventListener('keydown', handleExitSub);
    });

    mainLinks.forEach((link) => {
      link.addEventListener('keydown', handleArrowPress);
      link.addEventListener('keydown', handleExitMain);
      link.addEventListener('click', handleMainClick);
    });

    footerFocusable?.addEventListener('keydown', handleFooterFocus);

    const closeButton = closeRef?.current;
    closeButton?.addEventListener('keydown', handleCloseFocus);

    mainLinks[0].addEventListener('keydown', handleMainTabFocus);

    // Cleanup: Remove focus listeners and keydown event listener from the last sublink
    return () => {
      subLinks.forEach((link) => {
        link.onfocus = null;
      });

      footerFocusable?.removeEventListener('keydown', handleFooterFocus);
      closeButton?.addEventListener('keydown', handleCloseFocus);

      mainLinks.forEach((link) => {
        link.removeEventListener('keydown', handleArrowPress);
        link.removeEventListener('keydown', handleExitMain);
      });

      subLinks.forEach((link) => {
        link.removeEventListener('keydown', handleArrowPress);
        link.removeEventListener('keydown', handleExitSub);
        link.removeEventListener('click', handleMainClick);
      });

      mainLinks[0].removeEventListener('keydown', handleMainTabFocus);

      if (subLinks.length > 0) {
        const lastSubLink = subLinks[subLinks.length - 1];
        // Properly remove the event listener using the same function reference
        if (bp === 'md' || bp === 'lg') {
          lastSubLink.removeEventListener('keydown', handleTabOnLastSubLink);
        }

        if (bp === 'xl' || bp === '2xl' || bp === '3xl') {
          imageCardFocusable?.removeEventListener(
            'keydown',
            handleImageCardFocus,
          );
        }
        subLinks[0].removeEventListener('keydown', handleBackTabOnFirstSublink);
      }
    };
  }, [selected, currentSublinks, bp, setToggle, closeRef, isActive]);

  useEffect(() => {
    const mainLinks: HTMLButtonElement[] = Array.from(
      (mainLinksRef.current as HTMLDivElement)?.querySelectorAll('button'),
    ) as HTMLButtonElement[];

    const currentlySelectedLinkIndex = mainLinks?.findIndex(
      (link) => link.getAttribute('data-selected') == 'true',
    );

    mainLinks[currentlySelectedLinkIndex]?.focus();
  }, []);

  return (
    <div
      className="hidden grid-cols-4 md:grid"
      data-testid="nav-desktop-content"
    >
      <div
        aria-label="main menu"
        ref={mainLinksRef}
        tabIndex={-1} // Make the container focusable for managing focus
        className="col-span-2 flex grid-cols-1 flex-col justify-between gap-2 overflow-auto border-r border-nav pr-8.5 md:h-[calc(100dvh_-_130px)] xl:col-span-1 xl:h-[calc(100dvh_-_160px)]"
      >
        <OrNavbar.Links
          links={links}
          className="mb-6"
          selected={selected}
          setSelected={setSelected}
          topLevel
        />
        <FooterContent
          ref={footerRef}
          menuCloseRef={closeRef}
          chatProps={chatProps}
          phoneNumber={phoneNumber}
        />
      </div>

      <div
        ref={subLinkMenuRef}
        aria-label="sub-menu"
        className={classNames(
          'col-span-2 flex h-full flex-col overflow-auto pl-8 pt-11.5 md:h-[calc(100dvh_-_130px)] xl:col-span-1 xl:h-[calc(100dvh_-_160px)] xl:border-r xl:border-nav',
        )}
      >
        {currentSublinks.withoutTitle.length > 0 && (
          <ul
            className="flex flex-col gap-8 pb-13.5 pr-8.5"
            aria-label="sub-menu"
          >
            {currentSublinks.withoutTitle[0].links.link.map(
              (
                item: {
                  nav_link_content: ILinkContent['nav_link_content'];
                },
                index: number,
              ) => {
                const { title, href } = item.nav_link_content.content;

                return (
                  <li
                    aria-label="sub-menu-item"
                    key={index}
                    aria-posinset={index + 1}
                    aria-setsize={
                      currentSublinks.withoutTitle[0].links.link?.length
                    }
                  >
                    <AtNavLink
                      size={EClickableSize.LG}
                      target={
                        item.nav_link_content.target
                          ? EClickableTarget[item.nav_link_content.target]
                          : EClickableTarget.SELF
                      }
                      variant={ENavLinkVariants.MENU}
                      href={href}
                      label={title}
                      interactLocation="navbar"
                    ></AtNavLink>
                  </li>
                );
              },
            )}
          </ul>
        )}

        {Object.entries(currentSublinks.withTitle).map(
          ([title, items], index: number) => {
            return (
              <div key={`title-group-${index}`} className="flex flex-col gap-6">
                <AtText className="font-Figtree text-lg font-semibold uppercase leading-83 tracking-[0.04em] !text-[var(--color-text-tertiary)]">
                  {title}
                </AtText>
                <ul
                  className="flex flex-col gap-4 pb-13.5"
                  aria-label="sub-menu"
                >
                  {(items as unknown as IGroupedLinkContent[]).map(
                    (item: IGroupedLinkContent, idx: number) => {
                      const {
                        links: { link },
                      } = item;

                      return link.map((linkContent) => {
                        const {
                          nav_link_content: {
                            content: { title, href },
                            target,
                          },
                        } = linkContent;

                        return (
                          <li
                            aria-label="sub-menu-item"
                            key={`${title}_${idx}`}
                            aria-posinset={idx + 1}
                            aria-setsize={link?.length}
                          >
                            <AtNavLink
                              size={EClickableSize.LG}
                              target={
                                target
                                  ? EClickableTarget[
                                      target as keyof typeof EClickableTarget
                                    ]
                                  : EClickableTarget.SELF
                              }
                              variant={ENavLinkVariants.MENU}
                              href={href}
                              interactLocation="navbar"
                            >
                              {title}
                            </AtNavLink>
                          </li>
                        );
                      });
                    },
                  )}
                </ul>
              </div>
            );
          },
        )}
      </div>
      <div
        className="col-span-2 h-[calc(100dvh_-_160px)] md:hidden xl:block"
        ref={imageCardRef}
      >
        <ImageCard
          content={links[selected].nav_item.link_card[0]}
          className=""
        />
      </div>
    </div>
  );
};

interface IMobileNavBodyProps extends IMainCTAsProps {
  links: ILinkList[];
  chatProps: {
    content: string;
  };
  phoneNumber: string;
}

const MobileNavBody = ({
  links,
  topNavActions,
  closeRef,
  chatProps,
  phoneNumber,
}: IMobileNavBodyProps) => {
  useLockBodyScroll();
  const { setIsBannerVisible } = useGlobalBanner();

  const bannerKey = sessionStorage.getItem('global-promo-banner');

  useEffect(() => {
    if (bannerKey === null) {
      setIsBannerVisible(false);
    }

    return () => {
      if (bannerKey === null) {
        setIsBannerVisible(true);
      }
    };
  }, [bannerKey, setIsBannerVisible]);

  const { studioId, selectedText } = useLocation();
  const { handleOpen } = useSetLocationModal();
  return (
    <div
      className="col-span-4 block md:hidden"
      data-testid="nav-mobile-content"
    >
      <div className="landscape:h-[calc(100dvh_-_83px)] landscape:overflow-y-auto landscape:pb-20">
        <div className="mx-27 col-span-1 flex flex-col items-center justify-center gap-4 pt-0">
          <AtDivider
            customClasses="container w-[calc(100%_-_28px)]"
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-center">
              <AtSetLocationAction
                variant={ESetLocationActionVariants.NAV}
                onClick={() => handleOpen({ type: ESetLocationType.DEFAULT })}
                hasLocation={!!studioId}
              >
                {studioId ? selectedText : null}
              </AtSetLocationAction>
            </div>

            <div className="flex flex-row gap-1">
              <MainCTAs
                topNavActions={topNavActions}
                dataTestId="nav-mobile-main-ctas"
              />
            </div>
          </div>
          <AtDivider
            customClasses="container w-[calc(100%_-_28px)]"
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        </div>

        <div className="col-span-4 h-full max-h-[calc(100dvh_-_250px)] w-full overflow-y-auto pl-1.75 pr-2 pt-8 lg:px-0">
          <Accordion links={links} />
          <FooterContent
            phoneNumber={phoneNumber}
            menuCloseRef={closeRef}
            chatProps={chatProps}
          />
        </div>
      </div>
    </div>
  );
};

const FooterContent = forwardRef<HTMLDivElement, IFooterProps>(
  ({ menuCloseRef, chatProps, phoneNumber }, ref) => {
    const handleChatClick = () => {
      menuCloseRef?.current?.click();
      openChat('navbar');
    };

    const [invocaLoaded, setInvocaLoaded] = useState(false);

    useEffect(() => {
      const phoneEl = document.getElementById('nav-phone-number');
      if (!phoneEl) return;

      const obs = new MutationObserver(() => {
        setInvocaLoaded(true);
        obs.disconnect();
      });

      obs.observe(phoneEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      window.Invoca?.PNAPI?.run();

      return () => {
        obs.disconnect();
      };
    }, []);

    return (
      <div
        ref={ref}
        className="px-8 py-6 text-start text-38"
        data-theme="light"
      >
        <AtText size="lg" className="text-balance pb-4 font-light">
          {chatProps?.content ||
            'Questions? Our 24/7 chat can help you find answers'}
        </AtText>
        <div className="flex flex-col gap-y-5">
          <AtText
            className={classNames(invocaLoaded ? '' : 'invisible', 'pb-5')}
            type="span"
            id="nav-phone-number"
          >
            <span className="sr-only">Phone Number:</span>{' '}
            <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
          </AtText>

          <AtClickable
            variant={EClickableVariants.PRIMARY}
            type={EClickableTypes.LINK}
            ariaLabel="Open Chat"
            size={EClickableSize.LG}
            interactLocation="navbar"
            className="inline-block w-fit"
            onClick={handleChatClick}
          >
            Chat with us
          </AtClickable>
        </div>
      </div>
    );
  },
);

FooterContent.displayName = 'FooterContent';

const TopBarNavigation = ({
  setToggle,
  isToggled,
  topNavActions,
  closeRef,
}: ITopNavProps) => {
  const closeButtonColor = 'var(--color-white)';
  const openButtonColor = 'var(--color-ui-fill-primary)';
  const { topNav } = orNavBarVariants({ isToggled });
  const { studioId, selectedText } = useLocation();
  const { handleOpen } = useSetLocationModal();

  return (
    <div className={topNav()}>
      <div className={`absolute md:static md:col-span-1 lg:ml-0 lg:flex`}>
        <div className="flex flex-row items-center gap-10">
          <AtNavLink
            size={EClickableSize.LG}
            target={EClickableTarget.SELF}
            variant={ENavLinkVariants.CTA}
            aria-label={isToggled ? 'Close Menu' : 'Open Menu'}
            className="min-w-auto z-20 !m-0 box-border w-auto bg-transparent !p-0 text-left text-3xl lg:ml-0"
            onClick={() => setToggle(!isToggled)}
            ref={closeRef}
            aria-expanded={isToggled}
            interactLocation="navbar"
            shouldSendGTMEvent={false}
          >
            <span className="text-32 lg:text-58">
              {isToggled ? (
                <AtIcon
                  icon={EIcons.NAV_CLOSE}
                  className="rounded-full bg-navLink-selected"
                  color={closeButtonColor}
                />
              ) : (
                <AtIcon
                  icon={EIcons.NAV_OPEN}
                  className="bg-none"
                  color={openButtonColor}
                />
              )}
            </span>
          </AtNavLink>
          <div className="hidden md:block">
            <div className="flex flex-row items-center gap-2">
              <AtSetLocationAction
                hasLocation={!!studioId}
                variant={ESetLocationActionVariants.NAV}
                onClick={() => handleOpen({ type: ESetLocationType.DEFAULT })}
              >
                {studioId ? selectedText : null}
              </AtSetLocationAction>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`absolute col-span-2 flex w-full justify-center md:relative md:col-span-1 md:col-start-2`}
      >
        <AtLink
          href="/"
          ariaLabel="Go to home"
          type={EClickableTypes.LOGO}
          interactLocation="navbar"
          className="flex aspect-logo_m w-142 items-center justify-center !p-0 focus-visible:outline-offset-6 focus-visible:outline-logo lg:aspect-logo_d lg:w-258"
        >
          <AtLogo />
        </AtLink>
      </div>

      <div className="hidden flex-row items-center justify-end gap-3 md:inline-flex">
        <MainCTAs
          topNavActions={topNavActions}
          dataTestId="nav-desktop-main-ctas"
          closeRef={isToggled ? closeRef : undefined}
        />
      </div>
    </div>
  );
};

const Accordion = ({ links }: { links: ILinkList[] }) => {
  return (
    <MlAccordion
      className="mb-7 flex flex-col gap-2"
      dataTestId="nav-accordion"
    >
      {links?.map(({ nav_item: { title, sub_links } }, i) => {
        const grouped = groupLinksByTitle(sub_links);

        if (!title) return null;
        return (
          <MlAccordionItem
            title={title}
            index={i}
            key={`accordion-item-${i}`}
            intent={EAccordionItemIntents.NAV}
          >
            <ul className="flex flex-col gap-8" aria-label="sub-menu">
              {grouped.withoutTitle?.map((item) => {
                const {
                  links: { link },
                } = item;

                return link.map((linkItem, idx) => {
                  const {
                    nav_link_content: {
                      content: { title, href },
                    },
                  } = linkItem;
                  return (
                    <li
                      key={`${title}_${idx}`}
                      aria-label="sub-menu-item"
                      aria-setsize={link.length}
                      aria-posinset={idx + 1}
                    >
                      <AtNavLink
                        variant={ENavLinkVariants.MENU}
                        href={href}
                        size={EClickableSize.LG}
                        interactLocation="navbar"
                      >
                        {title}
                      </AtNavLink>
                    </li>
                  );
                });
              })}
            </ul>
            <div className="pb-12 pt-6">
              {Object.entries(
                grouped.withTitle.reduce<{
                  [key: string]: IGroupedLinkContent[];
                }>((acc, item) => {
                  // Group items by their title
                  (acc[item.links.title] = acc[item.links.title] || []).push(
                    item,
                  );
                  return acc;
                }, {}),
              ).map(([title, items], index) => {
                const {
                  links: { link: subLinks },
                } = items[0];

                return (
                  <div
                    key={`title-group-${index}`}
                    className="flex flex-col gap-4"
                  >
                    <AtText className="font-Figtree text-15 font-semibold uppercase leading-83 tracking-[0.04em] !text-skin-tertiary md:text-lg">
                      {title}
                    </AtText>
                    <ul className="flex flex-col gap-4" aria-label="sub-menu">
                      {subLinks.map((content, index) => {
                        const { title, href } =
                          content.nav_link_content.content;
                        const { target } = content.nav_link_content;
                        return (
                          <li
                            key={`${href} - ${index}`}
                            aria-label="sub-menu-item"
                            aria-posinset={index + 1}
                            aria-setsize={subLinks.length}
                          >
                            <AtNavLink
                              size={EClickableSize.LG}
                              target={
                                target
                                  ? EClickableTarget[
                                      target as keyof typeof EClickableTarget
                                    ]
                                  : EClickableTarget.SELF
                              }
                              variant={ENavLinkVariants.MENU}
                              href={href}
                              interactLocation="navbar"
                            >
                              {title}
                            </AtNavLink>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
            <AtDivider
              direction={EDividerDirectionalVariants.HORIZONTAL}
              intent={EDividerVariants.THIN}
              color={EDividerColors.GREY}
            />
          </MlAccordionItem>
        );
      })}
    </MlAccordion>
  );
};

const Links = ({
  links,
  className,
  setSelected,
  selected,
  topLevel,
}: IOrNavbarLinksProps) => {
  const bp = useBreakpoint();
  const isMobile = ['xs', 'sm', 'md'].includes(bp);

  const [hoverStates, setHoverStates] = useState(
    new Array(links.length).fill(false),
  );

  const handleMouseEnter = (index: number) => {
    if (isMobile) return;

    setHoverStates(hoverStates.map((state, i) => (i === index ? true : state)));
  };

  const handleMouseLeave = (index: number) => {
    if (isMobile) return;

    setHoverStates(
      hoverStates.map((state, i) => (i === index ? false : state)),
    );
  };
  return (
    <>
      <ul
        className={classNames(
          'box-border flex flex-col gap-4 px-8 pt-6 lg:px-0',
          className,
        )}
      >
        {links?.map((link, i) => {
          const { title } = link.nav_item;
          const currentSelected = i === selected;
          let hovered = false;
          return (
            <li
              aria-label="main-menu-item"
              className="nav-item"
              key={`${i}_${title}`}
              aria-posinset={i + 1}
              aria-setsize={links.length}
            >
              <AtNavLink
                size={EClickableSize.LG}
                label={title}
                variant={
                  topLevel ? ENavLinkVariants.CTA : ENavLinkVariants.MENU
                }
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={() => handleMouseLeave(i)}
                selected={currentSelected}
                onClick={() => setSelected(i)}
                target={EClickableTarget.SELF}
                interactLocation="navbar"
                className="flex w-full flex-row justify-between gap-10 text-balance text-left [&_span]:w-full [&_span]:justify-between [&_svg]:text-2xl"
              >
                {topLevel && (
                  <AtIcon
                    icon={EIcons.RIGHT_ARROW}
                    className={classNames(
                      selected === i ? 'opacity-1' : 'opacity-0',
                      'ml-2.5 min-w-6',
                    )}
                    color={
                      hoverStates[i] && currentSelected
                        ? 'black'
                        : 'var(--color-ui-knockout-primary)'
                    }
                  />
                )}
              </AtNavLink>
            </li>
          );
        })}
      </ul>
    </>
  );
};

const ImageCard = ({
  content,
  className,
}: {
  content: IMlCtaCardBlockCS;
  className: string;
}) => {
  const {
    content_title,
    icon: { icon },
    link,
    background_image,
    theme: { theme },
    description,
    show_cta,
  } = content;

  return (
    <div
      className={classNames(
        'relative z-auto col-span-2 col-start-3 mt-13.5 flex h-[calc(100%_-_52px)] flex-col justify-start !bg-transparent pl-8.5',
        className,
      )}
    >
      <MlCtaCard
        title={content_title}
        theme={EThemes[theme]}
        icon={icon}
        size={EClickableSize.LG}
        bgImage={background_image}
        description={description}
        headingElement="h2"
        showCta={show_cta}
        headingVariant="h3"
        link={link}
        interactLocation="navbar"
        className="!h-[calc(100%_-_52px)] !min-h-0 w-full"
      />
    </div>
  );
};

const SHARED_COOKIE_NAME = 'MySchumacherIsShared';
const MainCTAs = ({ topNavActions, dataTestId, closeRef }: IMainCTAsProps) => {
  const [isClient, setIsClient] = useState<boolean>(false);
  const { handleOpen } = useSetLocationModal();
  const { buildLoginUrl } = useContext(LoginModalContext);
  const { currentBuildStudio } = useLocation();
  const { logout } = useAuth();
  const router = useRouter();

  const drawerRef = useRef<HTMLDivElement>(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  // Parses and returns the shared cookie object or null
  const getParsedUserCookie = () => {
    try {
      const raw = getCookie(SHARED_COOKIE_NAME);
      if (!raw) return null;
      // DecodeURIComponent to handle encoded cookie values
      const parsed = JSON.parse(decodeURIComponent(raw as string));
      return parsed;
    } catch {
      return null;
    }
  };

  const parsedUserCookie = getParsedUserCookie();

  // Check logged-in state and that auth is still valid
  const isLoggedIn =
    parsedUserCookie?.AuthExpirationDateTime &&
    new Date(parsedUserCookie.AuthExpirationDateTime) > new Date();

  const firstName = isLoggedIn ? parsedUserCookie?.FirstName : null;

  const isLoginAction = (action: IAtLinkGlobalFieldProps) =>
    action.label.toLowerCase() === 'log in';

  const getActionLabel = (action: IAtLinkGlobalFieldProps) =>
    isLoggedIn && isLoginAction(action) ? `Hello, ${firstName}!` : action.label;

  const handleLogout = () => {
    logout();
    setShowAccountMenu(false);
  };

  const handleLoginClick = (action: IAtLinkGlobalFieldProps) => {
    if (isLoggedIn) {
      setShowAccountMenu(true);
    } else {
      closeRef?.current?.click();
      handleOpen({
        type: ESetLocationType.LOGIN,
        redirect: buildLoginUrl({
          LoginAction: 'LOGIN',
        }),
      });
    }
  };

  // close account drawer on escape, click or tap outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setShowAccountMenu(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowAccountMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Checks if href indicates a login action placeholder
  const checkIsLogin = (href: string) => {
    return href.includes(':openLoginModal');
  };

  // Modify href based on login status and studioId
  const currentHref = (href: string) => {
    if (checkIsLogin(href)) {
      return isLoggedIn
        ? undefined
        : buildLoginUrl({
            LoginAction: 'LOGIN',
            StudioId: currentBuildStudio?.Id
              ? String(currentBuildStudio.Id)
              : undefined,
          });
    }
    return href;
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <div data-testid={dataTestId} className="flex gap-1">
      {topNavActions.map((action, index) => {
        const theme =
          action.variant === 'DIMINUTIVE' ? EThemes.LIGHT : EThemes.DARK;
        const handleAccount = () => {
          if (!currentBuildStudio?.Id) {
            handleOpen({
              type: ESetLocationType.ACCOUNT,
              redirect: accountUrl,
            });
          } else {
            router.push(accountUrl);
          }
        };

        if (!isClient) return null;

        return (
          <span
            data-theme={theme}
            key={`${index}-label`}
            className="relative !bg-transparent"
          >
            <AtClickable
              {...action}
              icon={action.icon ? parseIconFromCMS(action.icon) : undefined}
              label={getActionLabel(action)}
              target={
                EClickableTarget[action.target as keyof typeof EClickableTarget]
              }
              href={
                currentBuildStudio
                  ? currentHref(action.url.href)
                  : action.url.href
              }
              variant={EClickableVariants[action.variant]}
              size={EClickableSize[action.size]}
              interactLocation="navbar"
              className="text-nowrap"
              onClick={() => handleLoginClick(action)}
              type={EClickableTypes.CTA}
              shouldSendGTMEvent={false}
            />
            <AnimatePresence initial={false}>
              {action.label.toLowerCase() === 'log in' && showAccountMenu && (
                <motion.div
                  data-theme="light"
                  role="menu"
                  key="account-menu"
                  data-testid="account-menu"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  ref={drawerRef}
                  className={classNames(
                    'absolute left-1/2 top-16 z-10 mt-3 w-auto -translate-x-1/2 rounded-30 bg-skin-primary px-6.5 py-9 shadow-card md:mt-0',
                  )}
                >
                  <div className="flex flex-col items-end text-right">
                    <AtClickable
                      onClick={handleAccount}
                      ariaLabel="My Account"
                      interactLocation="navbar"
                      className="p-0"
                    >
                      <AtText type="p" size="sm" className="whitespace-nowrap">
                        My Account
                      </AtText>
                    </AtClickable>
                    <AtClickable
                      type={EClickableTypes.LINK}
                      variant={EClickableVariants.TERTIARY}
                      ariaLabel="Log out"
                      interactLocation="navbar"
                      onClick={handleLogout}
                      className="pt-3"
                    >
                      <AtText type="p" size="sm" className="link-text-sm">
                        LOG OUT
                      </AtText>
                    </AtClickable>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </span>
        );
      })}
    </div>
  );
};

OrNavbar.Links = Links;
OrNavbar.MainCTAs = MainCTAs;
OrNavbar.Accordion = Accordion;
OrNavbar.MobileNavBody = MobileNavBody;
OrNavbar.DesktopNavBody = DesktopNavBody;
OrNavbar.ImageCard = ImageCard;
OrNavbar.TopBarNavigation = TopBarNavigation;
OrNavbar.FooterContent = FooterContent;
